import React, { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Button, message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { CloseOutlined, InfoCircleTwoTone, PlusOutlined } from '@ant-design/icons';
import { PRODUCT_MODE_MAP, isAppModule } from '@src/utils/constants';
import useSensitivity from '@src/components/aiAlerts/hooks/useSensitivity';
import { setAiAlertsSensitivity } from '@store/common/actions';
import { findDifferencesWithResult } from '@src/utils/aiAlerts/utils';
import {
  isAiAlertsCustomMetricsEnabled,
  aiAlertsCustomMetricsLimit,
  aiAlertsMetricsMetaSelector,
} from '@src/store/common/selectors';
import SensitivityByMetricForm from './SensitivityForm/SensitivityConfigForm';
import { tid } from '@src/utils/test';
import useMetricsMeta from '@src/hooks/useMetricsMeta';
import Header from './Header';
import { selectedProductAtom, fullSensitivityConfigAtom, drawerMetricsAtom } from '../atoms';
import MetricsDrawer from './MetricDrawer';
import { EditMetricWrapper, StyledDrawer } from './styled';
import { validateSensitivityConfigs } from '../popup/errorUtils';

const SensitivityDrawer = ({ isPopupOpen, cancelEdit }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { updateSensitivityMutation, data } = useSensitivity();
  const { forceUpdateUserDefinedMetricsMeta } = useMetricsMeta();
  const searchRef: any = useRef(null);
  const isAiAlertsCustomMetrics = useSelector(isAiAlertsCustomMetricsEnabled);
  const aiAlertsMetricsCustomMetricsLimit = useSelector(aiAlertsCustomMetricsLimit);
  const [openMetricDrawer, setOpenMetricDrawer] = useState(false);
  const [selectedProduct, setSelectedProduct] = useAtom(selectedProductAtom);
  const fullSensitivityData = useAtomValue(fullSensitivityConfigAtom);
  const [disable, setDisable] = useState(false);
  const aiAlertsMetricsMeta = useSelector(aiAlertsMetricsMetaSelector);
  const [drawerMetrics, setDrawerMetrics] = useAtom(drawerMetricsAtom);

  const selectedMetrics = aiAlertsMetricsMeta
    .filter((metric) => metric.isCustom)
    .map((metric) => metric.metricKey);

  const onOpenMetricDrawer = () => {
    if (!drawerMetrics?.length) {
      setDrawerMetrics(selectedMetrics);
    }
    setOpenMetricDrawer(true);
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus({
          cursor: 'start',
        });
      }
    }, 250);
    // refresh template metrics
    if (isAppModule) {
      forceUpdateUserDefinedMetricsMeta(); // refreshing once as store is defined on load
    }
  };

  const closeConfigDrawer = () => {
    cancelEdit();
    setDrawerMetrics(selectedMetrics);
  };

  const closeMetricDrawer = () => {
    setOpenMetricDrawer(false);
    cancelEdit();
    setDrawerMetrics(selectedMetrics);
  };

  const onBack = () => {
    setOpenMetricDrawer(false);
    setDrawerMetrics(selectedMetrics);
  };

  const saveSensitivity = async () => {
    const result = findDifferencesWithResult(fullSensitivityData, data);
    await updateSensitivityMutation.mutate(result, {
      onSuccess: async (response) => {
        const { data } = response;
        dispatch(setAiAlertsSensitivity(data));
        setIsLoading(false);
        message.success('Sensitivity updated successfully');
        cancelEdit();
      },
      onError: () => {
        message.error('There was an error while saving the sensitivity. Please try again later.');
        setIsLoading(false);
      },
    });
  };

  useEffect(() => {
    setDisable(validateSensitivityConfigs(fullSensitivityData));
  }, [selectedProduct, fullSensitivityData]);

  return (
    <>
      <StyledDrawer
        title={
          <Header
            selectedProduct={selectedProduct}
            onProductChange={(value) => setSelectedProduct(value)}
          />
        }
        width={window.innerWidth > 1000 ? 1000 : window.innerWidth - 100} // TODO: add media query
        closable={false}
        extra={<CloseOutlined onClick={closeConfigDrawer} />}
        open={isPopupOpen}
        // TODO: move these to styled.ts
        headerStyle={{ paddingBottom: 0 }}
        bodyStyle={{ padding: '0 35px 22px 22px' }}
        forceRender={true}
        footer={
          <div style={{ textAlign: 'right', marginTop: '20px' }}>
            <Button onClick={closeConfigDrawer}>Cancel</Button>
            <Button
              type="primary"
              style={{ marginLeft: '10px' }}
              onClick={saveSensitivity}
              loading={isLoading}
              disabled={disable}
            >
              Save
            </Button>
          </div>
        }
        {...tid('ai-alert-sensitivity-drawer')} // TODO: let QA know about this
      >
        {selectedProduct === PRODUCT_MODE_MAP.APP && isAiAlertsCustomMetrics && (
          <EditMetricWrapper>
            <Button
              type="primary"
              style={{ marginRight: '10px' }}
              onClick={onOpenMetricDrawer}
              disabled={aiAlertsMetricsCustomMetricsLimit == 0}
              ghost
            >
              <PlusOutlined />
              Metrics
            </Button>
            <InfoCircleTwoTone />
            {aiAlertsMetricsCustomMetricsLimit == 0 ? (
              <span>{`You don't have provision to add custom metrics.`}</span>
            ) : (
              <span>
                {` Configure thresholds for up to`} <b>{aiAlertsMetricsCustomMetricsLimit}</b>{' '}
                {`custom metrics`}
              </span>
            )}
          </EditMetricWrapper>
        )}
        <SensitivityByMetricForm />
      </StyledDrawer>
      {selectedProduct === PRODUCT_MODE_MAP.APP && isAiAlertsCustomMetrics && openMetricDrawer && (
        <MetricsDrawer
          isDrawerOpen={openMetricDrawer}
          setOpenMetricDrawer={setOpenMetricDrawer}
          onClose={closeMetricDrawer}
          onBack={onBack}
          searchRef={searchRef}
        />
      )}
    </>
  );
};

export default React.memo(SensitivityDrawer);
